import {
  useMemo,
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
  Ref,
} from "react";
import { RichTreeViewPro, useTreeViewApiRef } from "@mui/x-tree-view-pro";
import { Box } from "@mui/material";
import {
  IAbsenceGroup,
  IAbsenceGroupsOrderConfiguration,
} from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import { IAbsenceType } from "src/Pages/Company/Absences/AbsenceType/AbsenceType.models";
import transformAbsenceTypesToGroupedLists from "src/utils/Company/Absences/transformAbsenceTypesToGroupedLists";
import transformGroupedAbsenceTypesToTreeItems, {
  IAbsenceGroupTreeNode,
} from "src/Pages/Company/Absences/AbsenceGroupSorting/utils/transformGroupedAbsenceTypesToTreeItems";
import {
  applyOrderToItems,
  treeToOrderPayload,
  GROUP_ID_PREFIX,
} from "src/Pages/Company/Absences/AbsenceGroupSorting/utils/absenceGroupOrderUtils";
import { useFetchAbsenceGroupsOrderConfiguration } from "src/Pages/Company/Absences/api/absenceGroups.queries";
import __ from "src/utils/Translations";
import { Loader } from "src/Components/Loader/Loader";
import AbsenceGroupTreeItem, {
  absenceGroupColorMap,
} from "./AbsenceGroupTreeItem";

export interface IAbsenceGroupSortingRef {
  save: () => void;
}

interface IAbsenceGroupSortingProps {
  companyId: string;
  absenceGroups: IAbsenceGroup[] | undefined;
  absenceTypes: IAbsenceType[] | undefined;
  onSave: (payload: IAbsenceGroupsOrderConfiguration) => void;
}

const AbsenceGroupSorting = forwardRef(
  (
    {
      companyId,
      absenceGroups,
      absenceTypes,
      onSave,
    }: IAbsenceGroupSortingProps,
    ref: Ref<IAbsenceGroupSortingRef>
  ) => {
    const apiRef = useTreeViewApiRef();
    const { data: savedOrder, isLoading } =
      useFetchAbsenceGroupsOrderConfiguration(companyId);

    const { active } = useMemo(() => {
      const result = transformAbsenceTypesToGroupedLists(
        absenceGroups,
        absenceTypes,
        false
      );
      absenceGroupColorMap.clear();
      result.active.forEach((group) => {
        if (group.groupColor)
          absenceGroupColorMap.set(
            `${GROUP_ID_PREFIX}${group.groupId}`,
            group.groupColor
          );
      });
      return result;
    }, [absenceGroups, absenceTypes]);

    const baseItems = useMemo(
      () => transformGroupedAbsenceTypesToTreeItems(active),
      [active]
    );

    const [items, setItems] = useState<IAbsenceGroupTreeNode[]>(baseItems);

    // Apply saved order once it arrives, or reset when base items change
    useEffect(() => {
      setItems(
        savedOrder ? applyOrderToItems(baseItems, savedOrder) : baseItems
      );
    }, [baseItems, savedOrder]);

    const handleSave = () => {
      onSave(treeToOrderPayload(items));
    };

    useImperativeHandle(ref, () => ({ save: handleSave }));

    return (
      <Box sx={{ minWidth: 400, position: "relative", mt: "0.5rem" }}>
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Loader />
          </Box>
        )}
        {!isLoading && (
          <RichTreeViewPro
            apiRef={apiRef}
            items={items}
            itemsReordering
            defaultExpandedItems={items.map((i) => i.id)}
            slots={{ item: AbsenceGroupTreeItem }}
            onItemPositionChange={() => {
              const updatedTree = apiRef.current?.getItemTree() as
                | IAbsenceGroupTreeNode[]
                | undefined;
              if (updatedTree) setItems(updatedTree);
            }}
            canMoveItemToNewPosition={({
              itemId,
              oldPosition,
              newPosition,
            }) => {
              const isGroup = itemId.startsWith(GROUP_ID_PREFIX);

              // Groups can only reorder at root level — no nesting
              if (isGroup) return newPosition.parentId === null;

              // All other items must stay within the same parent
              return newPosition.parentId === oldPosition.parentId;
            }}
          />
        )}
      </Box>
    );
  }
);

export default AbsenceGroupSorting;
